import React, { useState, useEffect } from 'react';
import { useDashboard } from './DashboardContext';
import { coursesApi, uploadApi } from '../../services/api';
import useVisiblePolling from '../../hooks/useVisiblePolling';
import CourseBuilder from '../../components/CourseBuilder';
import {
  X,
  BookOpen,
  Users,
  Globe,
  Loader2,
  Check,
  Ticket,
  Plus,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Percent,
  DollarSign,
  Calendar,
  Hash,
  Link,
  Copy,
  AlertTriangle,
  CreditCard,
  CheckCircle,
  UserPlus,
  UserMinus,
  Activity
} from 'lucide-react';

// Helper to format period for display
const formatPeriod = (period) => {
  switch (period) {
    case 'month': return '/mo';
    case '3months': return '/3mo';
    case '6months': return '/6mo';
    case 'year': return '/yr';
    case 'one-time': return ' (Lifetime)';
    default: return '/mo';
  }
};

const AdminPanelPage = ({ modalsOnly = false }) => {
  const {
    users,
    courses,
    setCourses,
    memberships,
    channels,
    usersWithSubs,
    selectedUserForSubs,
    setSelectedUserForSubs,
    showMembershipModal,
    setShowMembershipModal,
    showChannelsModal,
    setShowChannelsModal,
    showUsersModal,
    setShowUsersModal,
    showCoursesModal,
    setShowCoursesModal,
    showCourseBuilder,
    setShowCourseBuilder,
    showAssignModal,
    setShowAssignModal,
    editingMembership,
    setEditingMembership,
    editingChannel,
    setEditingChannel,
    editingCourse,
    setEditingCourse,
    editingUser,
    setEditingUser,
    formatDate,
    handleOpenUserManagement,
    handleSaveMembership,
    handleAddNewMembership,
    handleDeleteMembership,
    handleSaveChannel,
    handleAddNewChannel,
    handleDeleteChannel,
    handleSaveUser,
    handleToggleUserStatus,
    handleDeleteUserPermanently,
    handleResetUserPassword,
    handleAssignMembership,
    handleRevokeMembership,
    handleAssignCourse,
    handleRevokeCourseAccess,
    coupons,
    handleCreateCoupon,
    handleUpdateCoupon,
    handleDeleteCoupon,
    handleToggleCoupon,
    failedPayments,
    failedPaymentsCount,
    loadFailedPayments,
    handleResolveFailedPayment,
    handleAddTestFailedPayment,
    handleDowngradeUserToFree,
    newMembers,
    newMembersCount,
    loadNewMembers,
    handleProcessNewMember,
    handleAddTestNewMember,
    handleSyncNewMemberAlerts,
    handleRefreshNewMemberData,
    handleSyncUserFromStripe,
    handleCreateStripeSubscription,
    loadMembershipActivity
  } = useDashboard();

  const [actionLoading, setActionLoading] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);
  const [copiedLink, setCopiedLink] = useState(null);
  const [showCouponsModal, setShowCouponsModal] = useState(false);
  const [showMembershipActivityModal, setShowMembershipActivityModal] = useState(false);
  const [membershipActivityTab, setMembershipActivityTab] = useState('new-members');
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [couponForm, setCouponForm] = useState({
    code: '',
    name: '',
    discount_type: 'percentage',
    discount_value: '',
    applies_to: 'all',
    product_ids: [],
    max_uses: '',
    max_uses_per_customer: 1,
    expires_at: '',
    minimum_purchase: '',
    duration: 'once',
    duration_months: ''
  });

  // Listen for custom events from AdminDashboard to open modals
  useEffect(() => {
    const handleOpenCoupons = () => {
      setShowCouponsModal(true);
    };
    const handleOpenMembershipActivity = () => {
      loadMembershipActivity();
      setShowMembershipActivityModal(true);
    };
    const handleOpenUsers = () => {
      setShowUsersModal(true);
    };
    const handleOpenCourses = () => {
      setShowCoursesModal(true);
    };
    const handleOpenMemberships = () => {
      setShowMembershipModal(true);
    };
    const handleOpenChannels = () => {
      setShowChannelsModal(true);
    };

    window.addEventListener('openCouponsModal', handleOpenCoupons);
    window.addEventListener('openMembershipActivityModal', handleOpenMembershipActivity);
    window.addEventListener('openUsersModal', handleOpenUsers);
    window.addEventListener('openCoursesModal', handleOpenCourses);
    window.addEventListener('openMembershipsModal', handleOpenMemberships);
    window.addEventListener('openChannelsModal', handleOpenChannels);

    return () => {
      window.removeEventListener('openCouponsModal', handleOpenCoupons);
      window.removeEventListener('openMembershipActivityModal', handleOpenMembershipActivity);
      window.removeEventListener('openUsersModal', handleOpenUsers);
      window.removeEventListener('openCoursesModal', handleOpenCourses);
      window.removeEventListener('openMembershipsModal', handleOpenMemberships);
      window.removeEventListener('openChannelsModal', handleOpenChannels);
    };
  }, [loadMembershipActivity, setShowUsersModal, setShowCoursesModal, setShowMembershipModal, setShowChannelsModal]);

  // Keep Membership Activity (new members + failed payments) fresh while modal is open.
  useVisiblePolling(async () => {
    await loadMembershipActivity();
  }, { intervalMs: 30000, enabled: showMembershipActivityModal, deps: [showMembershipActivityModal] });

  // Copy direct purchase link to clipboard
  const copyPurchaseLink = (type, id, name) => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/purchase/${type}/${id}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopiedLink(id);
      setTimeout(() => setCopiedLink(null), 2000);
    });
  };

  const showSuccessFeedback = (action) => {
    setActionSuccess(action);
    setTimeout(() => setActionSuccess(null), 2000);
  };

  const handleAssignMembershipWithFeedback = async (userId, membershipId) => {
    setActionLoading('assign-membership');
    await handleAssignMembership(userId, membershipId);
    setActionLoading(null);
    showSuccessFeedback('membership-assigned');
  };

  const handleRevokeMembershipWithFeedback = async (userId) => {
    setActionLoading('revoke-membership');
    await handleRevokeMembership(userId);
    setActionLoading(null);
    showSuccessFeedback('membership-revoked');
  };

  const handleAssignCourseWithFeedback = async (userId, courseId) => {
    setActionLoading('assign-course');
    await handleAssignCourse(userId, courseId);
    setActionLoading(null);
    showSuccessFeedback('course-assigned');
  };

  const handleRevokeCourseWithFeedback = async (userId, courseId) => {
    setActionLoading(`revoke-course-${courseId}`);
    await handleRevokeCourseAccess(userId, courseId);
    setActionLoading(null);
    showSuccessFeedback('course-revoked');
  };

  const handleDeleteCourse = async (courseId) => {
    try {
      await coursesApi.delete(courseId);
      const updatedCourses = courses.filter(c => c.id !== courseId);
      setCourses(updatedCourses);
    } catch (error) {
      console.error('Failed to delete course:', error);
      alert('Failed to delete course. Please try again.');
    }
  };

  const handleToggleArchiveCourse = async (courseId) => {
    try {
      const result = await coursesApi.toggleArchive(courseId);
      // Update local state
      setCourses(courses.map(c => 
        c.id === courseId ? { ...c, archived: result.archived } : c
      ));
    } catch (error) {
      console.error('Failed to toggle archive:', error);
      alert('Failed to update course. Please try again.');
    }
  };

  // Coupon form handlers
  const resetCouponForm = () => {
    setCouponForm({
      code: '',
      name: '',
      discount_type: 'percentage',
      discount_value: '',
      applies_to: 'all',
      product_ids: [],
      max_uses: '',
      max_uses_per_customer: 1,
      expires_at: '',
      minimum_purchase: '',
      duration: 'once',
      duration_months: ''
    });
    setEditingCoupon(null);
  };

  const handleEditCoupon = (coupon) => {
    setEditingCoupon(coupon);
    setCouponForm({
      code: coupon.code || '',
      name: coupon.name || '',
      discount_type: coupon.discount_type || 'percentage',
      discount_value: coupon.discount_value || '',
      applies_to: coupon.applies_to || 'all',
      product_ids: coupon.product_ids || [],
      max_uses: coupon.max_uses || '',
      max_uses_per_customer: coupon.max_uses_per_customer || 1,
      expires_at: coupon.expires_at ? coupon.expires_at.split('T')[0] : '',
      minimum_purchase: coupon.minimum_purchase || '',
      duration: coupon.duration || 'once',
      duration_months: coupon.duration_months || ''
    });
    setShowCouponsModal(true);
  };

  const handleSaveCoupon = async () => {
    if (!couponForm.code || !couponForm.name || !couponForm.discount_value) {
      alert('Please fill in all required fields (Code, Name, Discount Value)');
      return;
    }

    if (couponForm.applies_to === 'specific' && couponForm.product_ids.length === 0) {
      alert('Please select at least one product for this coupon to apply to');
      return;
    }

    if (couponForm.duration === 'repeating' && !couponForm.duration_months) {
      alert('Please specify the number of months for repeating duration');
      return;
    }

    const couponData = {
      code: couponForm.code,
      name: couponForm.name,
      discount_type: couponForm.discount_type,
      discount_value: parseFloat(couponForm.discount_value),
      applies_to: couponForm.applies_to,
      product_ids: couponForm.product_ids.length > 0 ? couponForm.product_ids : null,
      max_uses: couponForm.max_uses ? parseInt(couponForm.max_uses) : null,
      max_uses_per_customer: parseInt(couponForm.max_uses_per_customer) || 1,
      expires_at: couponForm.expires_at ? new Date(couponForm.expires_at).toISOString() : null,
      minimum_purchase: couponForm.minimum_purchase ? parseFloat(couponForm.minimum_purchase) : null,
      duration: couponForm.duration,
      duration_months: couponForm.duration === 'repeating' ? parseInt(couponForm.duration_months) : null
    };

    try {
      if (editingCoupon) {
        await handleUpdateCoupon(editingCoupon.id, couponData);
      } else {
        await handleCreateCoupon(couponData);
      }
      resetCouponForm();
    } catch (error) {
      alert(error.message || 'Failed to save coupon');
    }
  };

  return (
    <div className={modalsOnly ? 'admin-modals-only' : 'page-content'}>
      {!modalsOnly && (
        <>
          <h1 className="page-title">Admin Panel</h1>
          <p className="page-description">Manage site content and settings</p>
          
          <div className="admin-sections">
            <div className="admin-card">
              <h3 className="card-section-title">Membership Plans</h3>
              <p className="admin-desc">Add or edit membership tiers</p>
              <button className="admin-action-btn" onClick={() => setShowMembershipModal(true)}>Manage Memberships</button>
            </div>
            
            <div className="admin-card">
              <h3 className="card-section-title">Community Channels</h3>
              <p className="admin-desc">Create and manage chat channels</p>
              <button className="admin-action-btn" onClick={() => setShowChannelsModal(true)}>Manage Channels</button>
            </div>
            
            <div className="admin-card">
              <h3 className="card-section-title">User Management</h3>
              <p className="admin-desc">View and manage user accounts</p>
              <button className="admin-action-btn" onClick={handleOpenUserManagement}>Manage Users</button>
            </div>

            <div className="admin-card">
              <h3 className="card-section-title">Course Management</h3>
              <p className="admin-desc">Create and manage trading courses</p>
              <button className="admin-action-btn" onClick={() => setShowCoursesModal(true)}>Manage Courses</button>
            </div>

            <div className="admin-card">
              <h3 className="card-section-title">Coupon Codes</h3>
              <p className="admin-desc">Create discount codes for products</p>
              <button className="admin-action-btn" onClick={() => setShowCouponsModal(true)}>Manage Coupons</button>
            </div>

            <div className="admin-card mentorship-links-card">
              <h3 className="card-section-title">
                <CreditCard size={20} style={{ marginRight: '0.5rem' }} />
                Mentorship Links
              </h3>
              <p className="admin-desc">Hidden purchase links for mentorship plans</p>
              <div className="mentorship-links-list">
                <div className="mentorship-link-item">
                  <div className="mentorship-link-info">
                    <span className="mentorship-link-name">6 Month Mini Mentorship (Bi Weekly)</span>
                    <span className="mentorship-link-price">$1,500</span>
                  </div>
                  <button 
                    className={`copy-link-btn ${copiedLink === 'mentorship-bi-weekly' ? 'copied' : ''}`}
                    onClick={() => {
                      const link = `${window.location.origin}/mentorship/bi-weekly`;
                      navigator.clipboard.writeText(link).then(() => {
                        setCopiedLink('mentorship-bi-weekly');
                        setTimeout(() => setCopiedLink(null), 2000);
                      });
                    }}
                    data-testid="copy-biweekly-mentorship-link"
                  >
                    {copiedLink === 'mentorship-bi-weekly' ? <Check size={14} /> : <Copy size={14} />}
                    {copiedLink === 'mentorship-bi-weekly' ? 'Copied!' : 'Copy Link'}
                  </button>
                </div>
                <div className="mentorship-link-item">
                  <div className="mentorship-link-info">
                    <span className="mentorship-link-name">6 Month Mentorship (Weekly)</span>
                    <span className="mentorship-link-price">$5,000</span>
                  </div>
                  <button 
                    className={`copy-link-btn ${copiedLink === 'mentorship-weekly' ? 'copied' : ''}`}
                    onClick={() => {
                      const link = `${window.location.origin}/mentorship/weekly`;
                      navigator.clipboard.writeText(link).then(() => {
                        setCopiedLink('mentorship-weekly');
                        setTimeout(() => setCopiedLink(null), 2000);
                      });
                    }}
                    data-testid="copy-weekly-mentorship-link"
                  >
                    {copiedLink === 'mentorship-weekly' ? <Check size={14} /> : <Copy size={14} />}
                    {copiedLink === 'mentorship-weekly' ? 'Copied!' : 'Copy Link'}
                  </button>
                </div>
              </div>
            </div>

            <div className="admin-card membership-activity-card" data-testid="membership-activity-card">
              <div className="card-header-with-badge">
                <h3 className="card-section-title">
                  <Activity size={20} style={{ marginRight: '0.5rem' }} />
                  Membership Activity
                </h3>
                {(newMembersCount + failedPaymentsCount) > 0 && (
                  <span className="alert-badge">{newMembersCount + failedPaymentsCount}</span>
                )}
              </div>
              <p className="admin-desc">Track new signups and failed payments for TradingView access</p>
              <div className="activity-summary">
                {newMembersCount > 0 && (
                  <span className="activity-tag new-members">
                    <UserPlus size={14} /> {newMembersCount} new
                  </span>
                )}
                {failedPaymentsCount > 0 && (
                  <span className="activity-tag failed-payments">
                    <AlertTriangle size={14} /> {failedPaymentsCount} failed
                  </span>
                )}
              </div>
              <button 
                className={`admin-action-btn ${(newMembersCount + failedPaymentsCount) > 0 ? 'alert' : ''}`}
                onClick={() => {
                  loadMembershipActivity();
                  setShowMembershipActivityModal(true);
                }}
                data-testid="manage-membership-activity-btn"
              >
                Manage Activity
              </button>
            </div>
            
            <div className="admin-stats-grid">
              <div className="admin-stat-card">
                <h3>{users.length}</h3>
                <p>Total Users</p>
              </div>
              <div className="admin-stat-card">
                <h3>{users.filter(u => u.status === 'active').length}</h3>
                <p>Active Users</p>
              </div>
              <div className="admin-stat-card">
                <h3>{courses.length}</h3>
                <p>Total Courses</p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Membership Management Modal */}
      {showMembershipModal && (
        <div className="modal-overlay" onClick={() => setShowMembershipModal(false)}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Manage Memberships</h2>
              <button className="modal-close" onClick={() => setShowMembershipModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="memberships-list">
                {memberships.map(membership => (
                  <div key={membership.id} className={`membership-item ${membership.visible === false ? 'hidden-membership' : ''}`}>
                    <div className="membership-item-header">
                      <div className="membership-name-row">
                        <h4>{membership.name}</h4>
                        {membership.visible === false && (
                          <span className="hidden-badge">Hidden</span>
                        )}
                      </div>
                      <span className="membership-price">${membership.price}{formatPeriod(membership.period)}</span>
                    </div>
                    <ul className="membership-features-short">
                      {membership.features.map((feat, idx) => (
                        <li key={idx}>{feat}</li>
                      ))}
                    </ul>
                    <div className="membership-actions">
                      <button className="edit-membership-btn" onClick={() => setEditingMembership({...membership})}>Edit</button>
                      <button 
                        className={`copy-link-btn ${copiedLink === membership.id ? 'copied' : ''}`}
                        onClick={() => copyPurchaseLink('membership', membership.id, membership.name)}
                        title="Copy direct purchase link"
                      >
                        {copiedLink === membership.id ? <Check size={14} /> : <Link size={14} />}
                        {copiedLink === membership.id ? 'Copied!' : 'Copy Link'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <button className="add-new-membership-btn" onClick={handleAddNewMembership}>+ Add New Plan</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Membership Modal */}
      {editingMembership && (
        <div className="modal-overlay" onClick={() => setEditingMembership(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editingMembership.isNew ? 'Add' : 'Edit'} Membership</h2>
              <button className="modal-close" onClick={() => setEditingMembership(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-form-grid">
                <div className="form-group-modal">
                  <label>Plan Name</label>
                  <input 
                    type="text" 
                    value={editingMembership.name}
                    onChange={(e) => setEditingMembership({...editingMembership, name: e.target.value})}
                  />
                </div>
                <div className="form-group-modal">
                  <label>Price ($)</label>
                  <input 
                    type="number" 
                    value={editingMembership.price}
                    onChange={(e) => setEditingMembership({...editingMembership, price: parseFloat(e.target.value)})}
                    step="0.01"
                  />
                </div>
                <div className="form-group-modal">
                  <label>Billing Period</label>
                  <select
                    value={editingMembership.period || 'month'}
                    onChange={(e) => setEditingMembership({...editingMembership, period: e.target.value})}
                    className="form-select"
                  >
                    <option value="month">Monthly</option>
                    <option value="3months">Every 3 Months</option>
                    <option value="6months">Every 6 Months</option>
                    <option value="year">Yearly</option>
                    <option value="one-time">One-Time (Lifetime)</option>
                  </select>
                </div>
                <div className="form-group-modal visibility-toggle">
                  <label>Visibility</label>
                  <div className="toggle-container">
                    <button
                      type="button"
                      className={`visibility-btn ${editingMembership.visible !== false ? 'active' : ''}`}
                      onClick={() => setEditingMembership({...editingMembership, visible: true})}
                    >
                      <Globe size={16} />
                      Public
                    </button>
                    <button
                      type="button"
                      className={`visibility-btn ${editingMembership.visible === false ? 'active hidden-active' : ''}`}
                      onClick={() => setEditingMembership({...editingMembership, visible: false})}
                    >
                      <Users size={16} />
                      Hidden
                    </button>
                  </div>
                  <span className="visibility-hint">
                    {editingMembership.visible === false 
                      ? 'This plan won\'t appear on the homepage. Use for private/invite-only plans.' 
                      : 'This plan will be visible on the homepage.'}
                  </span>
                </div>
                <div className="form-group-modal full-width">
                  <label>Features (one per line)</label>
                  <textarea 
                    rows="6"
                    value={editingMembership.features.join('\n')}
                    onChange={(e) => setEditingMembership({...editingMembership, features: e.target.value.split('\n')})}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              {!editingMembership.isNew && (
                <button className="delete-btn" onClick={() => handleDeleteMembership(editingMembership.id)}>Delete</button>
              )}
              <button className="cancel-button" onClick={() => setEditingMembership(null)}>Cancel</button>
              <button className="add-trade-modal-button" onClick={handleSaveMembership}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Channels Management Modal */}
      {showChannelsModal && (
        <div className="modal-overlay" onClick={() => setShowChannelsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Manage Channels</h2>
              <button className="modal-close" onClick={() => setShowChannelsModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="channels-management-list">
                {channels.filter(c => c.visible).map(channel => (
                  <div key={channel.id} className="channel-management-item">
                    <div className="channel-item-info">
                      <span className="channel-hash">#</span>
                      <span className="channel-name-text">{channel.name}</span>
                      <span className="channel-label-text">{channel.label}</span>
                    </div>
                    <button className="edit-channel-btn" onClick={() => setEditingChannel({...channel})}>Edit</button>
                  </div>
                ))}
              </div>
              <button className="add-new-channel-btn" onClick={handleAddNewChannel}>+ Add New Channel</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Channel Modal */}
      {editingChannel && (
        <div className="modal-overlay" onClick={() => setEditingChannel(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editingChannel.isNew ? 'Add' : 'Edit'} Channel</h2>
              <button className="modal-close" onClick={() => setEditingChannel(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-form-grid">
                <div className="form-group-modal">
                  <label>Channel Name</label>
                  <input 
                    type="text" 
                    value={editingChannel.name}
                    onChange={(e) => setEditingChannel({...editingChannel, name: e.target.value})}
                    placeholder="e.g., trading-tips"
                  />
                </div>
                <div className="form-group-modal">
                  <label>Category Label</label>
                  <select
                    value={editingChannel.label}
                    onChange={(e) => setEditingChannel({...editingChannel, label: e.target.value})}
                  >
                    <option value="Community">Community</option>
                    <option value="Announcements">Announcements</option>
                    <option value="Trading">Trading</option>
                    <option value="Education">Education</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              {!editingChannel.isNew && (
                <button className="delete-btn" onClick={() => handleDeleteChannel(editingChannel.id)}>Delete</button>
              )}
              <button className="cancel-button" onClick={() => setEditingChannel(null)}>Cancel</button>
              <button className="add-trade-modal-button" onClick={handleSaveChannel}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Users Management Modal */}
      {showUsersModal && (
        <div className="modal-overlay" onClick={() => { setShowUsersModal(false); setSelectedUserForSubs(null); }}>
          <div className="modal-content extra-large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">User Management</h2>
              <button className="modal-close" onClick={() => { setShowUsersModal(false); setSelectedUserForSubs(null); }}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body user-management-body">
              <div className="users-list-panel">
                <h3 className="panel-title">All Users ({usersWithSubs.length})</h3>
                <div className="users-list-scroll">
                  {usersWithSubs.map(u => (
                    <div 
                      key={u.id} 
                      className={`user-list-item ${selectedUserForSubs?.id === u.id ? 'selected' : ''}`}
                      onClick={() => setSelectedUserForSubs(u)}
                    >
                      <div className="user-list-avatar">
                        {u.profile_picture ? (
                          <img src={uploadApi.getProfilePictureUrl(u.profile_picture)} alt="" />
                        ) : (
                          <span>{u.name?.charAt(0)?.toUpperCase() || 'U'}</span>
                        )}
                      </div>
                      <div className="user-list-info">
                        <div className="user-list-name">{u.name}</div>
                        <div className="user-list-email">{u.email}</div>
                        {u.birthday && (
                          <div className="user-list-birthday">🎂 {u.birthday}</div>
                        )}
                        <div className="user-list-badges">
                          <span className={`status-badge ${u.status}`}>{u.status}</span>
                          {u.membership && (
                            <span className="membership-badge">{u.membership.name}</span>
                          )}
                          {u.enrollments?.length > 0 && (
                            <span className="courses-badge">{u.enrollments.length} course(s)</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="user-details-panel">
                {selectedUserForSubs ? (
                  <>
                    <div className="user-details-header">
                      <div className="user-details-avatar">
                        {selectedUserForSubs.profile_picture ? (
                          <img src={uploadApi.getProfilePictureUrl(selectedUserForSubs.profile_picture)} alt="" />
                        ) : (
                          <span>{selectedUserForSubs.name?.charAt(0)?.toUpperCase() || 'U'}</span>
                        )}
                      </div>
                      <div className="user-details-info">
                        <h3>{selectedUserForSubs.name}</h3>
                        <p>{selectedUserForSubs.email}</p>
                        <p className="join-date">Member since: {formatDate(selectedUserForSubs.join_date)}</p>
                        {selectedUserForSubs.birthday && (
                          <p className="birthday-info">
                            🎂 Birthday: {selectedUserForSubs.birthday}
                          </p>
                        )}
                        {selectedUserForSubs.trading_view_username && (
                          <p className="tradingview-username">
                            <Globe size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                            TradingView: {selectedUserForSubs.trading_view_username}
                          </p>
                        )}
                      </div>
                      <div className="user-details-actions">
                        <button 
                          className={`status-toggle-btn ${selectedUserForSubs.status}`}
                          onClick={() => handleToggleUserStatus(selectedUserForSubs.id)}
                        >
                          {selectedUserForSubs.status === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                        <button 
                          className="sync-stripe-btn"
                          onClick={() => handleSyncUserFromStripe(selectedUserForSubs.email, selectedUserForSubs.id)}
                          title="Sync membership from Stripe"
                          style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', marginRight: '8px' }}
                        >
                          Sync from Stripe
                        </button>
                        {selectedUserForSubs.membership && !selectedUserForSubs.stripe_subscription_id && (
                          <button 
                            className="create-subscription-btn"
                            onClick={() => handleCreateStripeSubscription(selectedUserForSubs.id, selectedUserForSubs.name)}
                            title="Create Stripe subscription for auto-renewal"
                            style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', marginRight: '8px' }}
                          >
                            Create Subscription
                          </button>
                        )}
                        <button 
                          className="delete-user-btn"
                          onClick={() => handleDeleteUserPermanently(selectedUserForSubs.id)}
                          title="Permanently delete this user"
                        >
                          Delete User
                        </button>
                        <button 
                          className="reset-password-btn"
                          onClick={() => handleResetUserPassword(selectedUserForSubs.email)}
                          title="Reset user's password"
                          style={{ background: 'linear-gradient(135deg, #10b981, #059669)', marginTop: '8px' }}
                        >
                          Reset Password
                        </button>
                      </div>
                    </div>

                    <div className="subscription-section tradingview-section">
                      <div className="section-header">
                        <h4><Globe size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />TradingView Username</h4>
                      </div>
                      <div className="tradingview-display">
                        {selectedUserForSubs.trading_view_username ? (
                          <a 
                            href={`https://www.tradingview.com/u/${selectedUserForSubs.trading_view_username}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="tradingview-link"
                          >
                            @{selectedUserForSubs.trading_view_username}
                          </a>
                        ) : (
                          <p className="no-subscription">No TradingView username set</p>
                        )}
                      </div>
                    </div>

                    <div className="subscription-section">
                      <div className="section-header">
                        <h4>Membership Plan</h4>
                        <button 
                          className="assign-btn"
                          onClick={() => setShowAssignModal({ type: 'membership', userId: selectedUserForSubs.id })}
                        >
                          + Assign Plan
                        </button>
                      </div>
                      {selectedUserForSubs.membership ? (
                        <div className="subscription-item">
                          <div className="subscription-info">
                            <span className="subscription-name">{selectedUserForSubs.membership.name}</span>
                            <span className="subscription-price">${selectedUserForSubs.membership.price}{formatPeriod(selectedUserForSubs.membership.period)}</span>
                            <span className="subscription-date">Since: {formatDate(selectedUserForSubs.membership.assigned_at)}</span>
                          </div>
                          <button 
                            className={`revoke-btn ${actionSuccess === 'membership-revoked' ? 'success' : ''}`}
                            onClick={() => handleRevokeMembershipWithFeedback(selectedUserForSubs.id)}
                            disabled={actionLoading === 'revoke-membership'}
                          >
                            {actionLoading === 'revoke-membership' ? (
                              <><Loader2 size={14} className="spinner" /> Revoking...</>
                            ) : actionSuccess === 'membership-revoked' ? (
                              <><Check size={14} /> Revoked!</>
                            ) : (
                              'Revoke'
                            )}
                          </button>
                        </div>
                      ) : (
                        <p className="no-subscription">No membership assigned</p>
                      )}
                    </div>

                    <div className="subscription-section">
                      <div className="section-header">
                        <h4>Course Access</h4>
                        <button 
                          className="assign-btn"
                          onClick={() => setShowAssignModal({ type: 'course', userId: selectedUserForSubs.id })}
                        >
                          + Grant Access
                        </button>
                      </div>
                      {selectedUserForSubs.enrollments?.length > 0 ? (
                        <div className="enrollments-list">
                          {selectedUserForSubs.enrollments.map(enrollment => (
                            <div key={enrollment.enrollment_id} className="subscription-item">
                              <div className="subscription-info">
                                <span className="subscription-name">{enrollment.course_title}</span>
                                <span className="subscription-price">
                                  {enrollment.course_price > 0 ? `$${enrollment.course_price}` : 'Free'}
                                </span>
                                <span className="subscription-date">
                                  Enrolled: {formatDate(enrollment.enrolled_at)}
                                </span>
                              </div>
                              <button 
                                className={`revoke-btn ${actionSuccess === 'course-revoked' ? 'success' : ''}`}
                                onClick={() => handleRevokeCourseWithFeedback(selectedUserForSubs.id, enrollment.course_id)}
                                disabled={actionLoading === `revoke-course-${enrollment.course_id}`}
                              >
                                {actionLoading === `revoke-course-${enrollment.course_id}` ? (
                                  <><Loader2 size={14} className="spinner" /> Revoking...</>
                                ) : (
                                  'Revoke'
                                )}
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-subscription">No course enrollments</p>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="select-user-prompt">
                    <Users size={48} />
                    <p>Select a user to view their subscriptions</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Membership/Course Modal */}
      {showAssignModal && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {showAssignModal.type === 'membership' ? 'Assign Membership Plan' : 'Grant Course Access'}
              </h2>
              <button className="modal-close" onClick={() => setShowAssignModal(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {showAssignModal.type === 'membership' ? (
                <div className="assign-list">
                  {memberships.filter(m => m.visible !== false).map(membership => (
                    <div 
                      key={membership.id} 
                      className={`assign-item ${actionLoading === 'assign-membership' ? 'loading' : ''}`}
                      onClick={() => !actionLoading && handleAssignMembershipWithFeedback(showAssignModal.userId, membership.id)}
                    >
                      <div className="assign-item-info">
                        <span className="assign-item-name">{membership.name}</span>
                        <span className="assign-item-price">${membership.price}{formatPeriod(membership.period)}</span>
                      </div>
                      <span className="assign-item-features">
                        {membership.features?.slice(0, 2).join(', ')}
                        {membership.features?.length > 2 && '...'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="assign-list">
                  {courses.map(course => (
                    <div 
                      key={course.id} 
                      className={`assign-item ${actionLoading === 'assign-course' ? 'loading' : ''}`}
                      onClick={() => !actionLoading && handleAssignCourseWithFeedback(showAssignModal.userId, course.id)}
                    >
                      <div className="assign-item-info">
                        <span className="assign-item-name">{course.title}</span>
                        <span className="assign-item-price">
                          {course.price > 0 ? `$${course.price}` : 'Free'}
                        </span>
                      </div>
                      <span className="assign-item-features">
                        {course.sections?.length || 0} sections • {course.instructor}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {actionLoading && (
                <div className="assign-loading">
                  <Loader2 size={20} className="spinner" />
                  <span>Assigning...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="modal-overlay" onClick={() => setEditingUser(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Edit User</h2>
              <button className="modal-close" onClick={() => setEditingUser(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-form-grid">
                <div className="form-group-modal">
                  <label>Name</label>
                  <input 
                    type="text" 
                    value={editingUser.name}
                    onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
                  />
                </div>
                <div className="form-group-modal">
                  <label>Email</label>
                  <input 
                    type="email" 
                    value={editingUser.email}
                    onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                  />
                </div>
                <div className="form-group-modal">
                  <label>Birthday (MM-DD)</label>
                  <input 
                    type="text" 
                    placeholder="e.g. 02-06"
                    value={editingUser.birthday || ''}
                    onChange={(e) => setEditingUser({...editingUser, birthday: e.target.value})}
                  />
                </div>
                <div className="form-group-modal">
                  <label>Status</label>
                  <select
                    value={editingUser.status}
                    onChange={(e) => setEditingUser({...editingUser, status: e.target.value})}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-button" onClick={() => setEditingUser(null)}>Cancel</button>
              <button className="add-trade-modal-button" onClick={handleSaveUser}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Course Management Modal */}
      {showCoursesModal && (
        <div className="modal-overlay" onClick={() => setShowCoursesModal(false)}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Course Management</h2>
              <button className="modal-close" onClick={() => setShowCoursesModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="courses-header-actions">
                <button 
                  className="create-course-btn"
                  onClick={() => {
                    setEditingCourse(null);
                    setShowCourseBuilder(true);
                  }}
                >
                  <BookOpen size={18} />
                  Create New Course
                </button>
              </div>

              <div className="courses-list-section">
                <h3 className="section-subtitle">All Courses ({courses.length})</h3>
                {courses.length === 0 ? (
                  <p className="no-courses-text">No courses created yet. Click &quot;Create New Course&quot; to get started.</p>
                ) : (
                  <div className="courses-admin-list">
                    {courses.map(course => (
                      <div key={course.id} className={`course-admin-item ${course.archived ? 'archived' : ''}`}>
                        <div className="course-admin-info">
                          <h4>
                            {course.title}
                            {course.archived && <span className="archived-badge">Archived</span>}
                          </h4>
                          <p>{course.description}</p>
                          <div className="course-meta">
                            <span>Instructor: {course.instructor}</span>
                            <span>Price: ${course.price}</span>
                            <span className="sections-count">
                              {course.sections?.length || 0} sections
                            </span>
                            <span className="episodes-count">
                              {course.sections?.reduce((acc, s) => acc + (s.episodes?.length || 0), 0) || 0} episodes
                            </span>
                          </div>
                        </div>
                        <div className="course-admin-actions">
                          <button 
                            className={`copy-link-btn ${copiedLink === course.id ? 'copied' : ''}`}
                            onClick={() => copyPurchaseLink('course', course.id, course.title)}
                            title="Copy direct purchase link"
                          >
                            {copiedLink === course.id ? <Check size={14} /> : <Link size={14} />}
                            {copiedLink === course.id ? 'Copied!' : 'Copy Link'}
                          </button>
                          <button 
                            className="edit-btn"
                            onClick={() => {
                              setEditingCourse(course);
                              setShowCourseBuilder(true);
                            }}
                          >
                            Edit
                          </button>
                          <button 
                            className={`archive-btn ${course.archived ? 'archived' : ''}`}
                            onClick={() => handleToggleArchiveCourse(course.id)}
                          >
                            {course.archived ? 'Unarchive' : 'Archive'}
                          </button>
                          <button 
                            className="delete-btn" 
                            onClick={() => handleDeleteCourse(course.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Course Builder Modal */}
      {showCourseBuilder && (
        <CourseBuilder 
          course={editingCourse}
          onSave={async () => {
            try {
              const updatedCourses = await coursesApi.getAll();
              setCourses(updatedCourses);
            } catch (error) {
              console.error('Failed to refresh courses:', error);
            }
            setShowCourseBuilder(false);
            setEditingCourse(null);
          }}
          onClose={() => {
            setShowCourseBuilder(false);
            setEditingCourse(null);
          }}
        />
      )}

      {/* Coupon Management Modal */}
      {showCouponsModal && (
        <div className="modal-overlay" onClick={() => { setShowCouponsModal(false); resetCouponForm(); }}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                <Ticket size={24} />
                Manage Coupons
              </h2>
              <button className="modal-close" onClick={() => { setShowCouponsModal(false); resetCouponForm(); }}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {/* Coupon Form */}
              <div className="coupon-form-section">
                <h3 className="subsection-title">{editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}</h3>
                <div className="coupon-form-grid">
                  <div className="form-group">
                    <label>Coupon Code *</label>
                    <input
                      type="text"
                      value={couponForm.code}
                      onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
                      placeholder="e.g., SAVE20"
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Internal Name *</label>
                    <input
                      type="text"
                      value={couponForm.name}
                      onChange={(e) => setCouponForm({ ...couponForm, name: e.target.value })}
                      placeholder="e.g., Summer Sale 20%"
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Discount Type</label>
                    <select
                      value={couponForm.discount_type}
                      onChange={(e) => setCouponForm({ ...couponForm, discount_type: e.target.value })}
                      className="form-select"
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount ($)</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Discount Value *</label>
                    <div className="input-with-icon">
                      {couponForm.discount_type === 'percentage' ? <Percent size={16} /> : <DollarSign size={16} />}
                      <input
                        type="number"
                        value={couponForm.discount_value}
                        onChange={(e) => setCouponForm({ ...couponForm, discount_value: e.target.value })}
                        placeholder={couponForm.discount_type === 'percentage' ? '20' : '50'}
                        className="form-input"
                        min="0"
                        max={couponForm.discount_type === 'percentage' ? '100' : undefined}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Applies To</label>
                    <select
                      value={couponForm.applies_to}
                      onChange={(e) => setCouponForm({ ...couponForm, applies_to: e.target.value, product_ids: [] })}
                      className="form-select"
                    >
                      <option value="all">All Products</option>
                      <option value="courses">All Courses</option>
                      <option value="memberships">All Memberships</option>
                      <option value="specific">Specific Products</option>
                    </select>
                  </div>
                  {couponForm.applies_to === 'specific' && (
                    <div className="form-group full-width product-selection-group">
                      <label>Select Products</label>
                      <div className="product-selection-container">
                        {courses.length > 0 && (
                          <div className="product-category">
                            <h5 className="product-category-title">
                              <BookOpen size={16} />
                              Courses
                            </h5>
                            {courses.map(course => (
                              <label key={course.id} className="product-checkbox-item">
                                <input
                                  type="checkbox"
                                  checked={couponForm.product_ids.includes(course.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setCouponForm({
                                        ...couponForm,
                                        product_ids: [...couponForm.product_ids, course.id]
                                      });
                                    } else {
                                      setCouponForm({
                                        ...couponForm,
                                        product_ids: couponForm.product_ids.filter(id => id !== course.id)
                                      });
                                    }
                                  }}
                                />
                                <span className="product-name">{course.title}</span>
                                <span className="product-price">${course.price}</span>
                              </label>
                            ))}
                          </div>
                        )}
                        {memberships.length > 0 && (
                          <div className="product-category">
                            <h5 className="product-category-title">
                              <Ticket size={16} />
                              Memberships
                            </h5>
                            {memberships.map(membership => (
                              <label key={membership.id} className={`product-checkbox-item ${membership.visible === false ? 'hidden-product' : ''}`}>
                                <input
                                  type="checkbox"
                                  checked={couponForm.product_ids.includes(membership.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setCouponForm({
                                        ...couponForm,
                                        product_ids: [...couponForm.product_ids, membership.id]
                                      });
                                    } else {
                                      setCouponForm({
                                        ...couponForm,
                                        product_ids: couponForm.product_ids.filter(id => id !== membership.id)
                                      });
                                    }
                                  }}
                                />
                                <span className="product-name">
                                  {membership.name}
                                  {membership.visible === false && <span className="hidden-tag">(Hidden)</span>}
                                </span>
                                <span className="product-price">${membership.price}{formatPeriod(membership.period)}</span>
                              </label>
                            ))}
                          </div>
                        )}
                        {courses.length === 0 && memberships.length === 0 && (
                          <p className="no-products-message">No courses or memberships available yet.</p>
                        )}
                      </div>
                      {couponForm.product_ids.length > 0 && (
                        <p className="selected-count">{couponForm.product_ids.length} product(s) selected</p>
                      )}
                    </div>
                  )}
                  <div className="form-group">
                    <label>Expiration Date</label>
                    <div className="input-with-icon">
                      <Calendar size={16} />
                      <input
                        type="date"
                        value={couponForm.expires_at}
                        onChange={(e) => setCouponForm({ ...couponForm, expires_at: e.target.value })}
                        className="form-input"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Max Total Uses</label>
                    <div className="input-with-icon">
                      <Hash size={16} />
                      <input
                        type="number"
                        value={couponForm.max_uses}
                        onChange={(e) => setCouponForm({ ...couponForm, max_uses: e.target.value })}
                        placeholder="Unlimited"
                        className="form-input"
                        min="1"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Uses Per Customer</label>
                    <input
                      type="number"
                      value={couponForm.max_uses_per_customer}
                      onChange={(e) => setCouponForm({ ...couponForm, max_uses_per_customer: e.target.value })}
                      className="form-input"
                      min="1"
                    />
                  </div>
                  <div className="form-group">
                    <label>Minimum Purchase ($)</label>
                    <div className="input-with-icon">
                      <DollarSign size={16} />
                      <input
                        type="number"
                        value={couponForm.minimum_purchase}
                        onChange={(e) => setCouponForm({ ...couponForm, minimum_purchase: e.target.value })}
                        placeholder="No minimum"
                        className="form-input"
                        min="0"
                      />
                    </div>
                  </div>
                </div>

                {/* Duration Section */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Duration</label>
                    <select
                      value={couponForm.duration}
                      onChange={(e) => setCouponForm({ ...couponForm, duration: e.target.value, duration_months: '' })}
                      className="form-select"
                    >
                      <option value="once">Once</option>
                      <option value="repeating">Repeating</option>
                      <option value="forever">Forever</option>
                    </select>
                    <span className="field-hint">
                      {couponForm.duration === 'once' && 'Discount applies to the first payment only'}
                      {couponForm.duration === 'repeating' && 'Discount applies for a set number of months'}
                      {couponForm.duration === 'forever' && 'Discount applies to all future payments'}
                    </span>
                  </div>
                  {couponForm.duration === 'repeating' && (
                    <div className="form-group">
                      <label>Number of Months</label>
                      <input
                        type="number"
                        value={couponForm.duration_months}
                        onChange={(e) => setCouponForm({ ...couponForm, duration_months: e.target.value })}
                        placeholder="e.g., 3"
                        className="form-input"
                        min="1"
                        required
                      />
                    </div>
                  )}
                </div>

                <div className="coupon-form-actions">
                  <button onClick={handleSaveCoupon} className="btn-primary">
                    {editingCoupon ? 'Update Coupon' : 'Create Coupon'}
                  </button>
                  {editingCoupon && (
                    <button onClick={resetCouponForm} className="btn-secondary">
                      Cancel Edit
                    </button>
                  )}
                </div>
              </div>

              {/* Existing Coupons List */}
              <div className="coupons-list-section">
                <h3 className="subsection-title">Existing Coupons ({coupons?.length || 0})</h3>
                {coupons && coupons.length > 0 ? (
                  <div className="coupons-table">
                    <div className="coupons-table-header">
                      <span>Code</span>
                      <span>Name</span>
                      <span>Discount</span>
                      <span>Duration</span>
                      <span>Applies To</span>
                      <span>Uses</span>
                      <span>Status</span>
                      <span>Actions</span>
                    </div>
                    {coupons.map(coupon => {
                      // Helper to get product names for display
                      const getAppliesToDisplay = () => {
                        if (coupon.applies_to === 'all') return 'All Products';
                        if (coupon.applies_to === 'courses') return 'All Courses';
                        if (coupon.applies_to === 'memberships') return 'All Memberships';
                        if (coupon.applies_to === 'specific' && coupon.product_ids?.length > 0) {
                          const productNames = coupon.product_ids.map(id => {
                            const course = courses.find(c => c.id === id);
                            if (course) return course.title;
                            const membership = memberships.find(m => m.id === id);
                            if (membership) return membership.name;
                            return id.substring(0, 8) + '...';
                          });
                          if (productNames.length <= 2) return productNames.join(', ');
                          return `${productNames.slice(0, 2).join(', ')} +${productNames.length - 2} more`;
                        }
                        return 'All Products';
                      };

                      return (
                        <div key={coupon.id} className={`coupons-table-row ${!coupon.is_active ? 'inactive' : ''}`}>
                          <span className="coupon-code">{coupon.code}</span>
                          <span className="coupon-name">{coupon.name}</span>
                          <span className="coupon-discount">
                            {coupon.discount_type === 'percentage' 
                              ? `${coupon.discount_value}%` 
                              : `$${coupon.discount_value}`}
                          </span>
                          <span className="coupon-duration">
                            {coupon.duration === 'once' && 'Once'}
                            {coupon.duration === 'repeating' && `${coupon.duration_months} months`}
                            {coupon.duration === 'forever' && 'Forever'}
                            {!coupon.duration && 'Once'}
                          </span>
                          <span className="coupon-applies-to" title={coupon.applies_to === 'specific' ? coupon.product_ids?.join(', ') : ''}>
                            {getAppliesToDisplay()}
                          </span>
                          <span className="coupon-uses">
                            {coupon.current_uses || 0}{coupon.max_uses ? `/${coupon.max_uses}` : ''}
                          </span>
                          <span className={`coupon-status ${coupon.is_active ? 'active' : 'inactive'}`}>
                            {coupon.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <span className="coupon-actions">
                            <button 
                              onClick={() => handleToggleCoupon(coupon.id)} 
                              className="action-btn"
                              title={coupon.is_active ? 'Deactivate' : 'Activate'}
                            >
                              {coupon.is_active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                            </button>
                            <button 
                              onClick={() => handleEditCoupon(coupon)} 
                              className="action-btn"
                              title="Edit"
                            >
                              <Edit2 size={18} />
                            </button>
                            <button 
                              onClick={() => handleDeleteCoupon(coupon.id)} 
                              className="action-btn danger"
                              title="Delete"
                            >
                              <Trash2 size={18} />
                            </button>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="no-coupons">No coupons created yet. Create your first coupon above!</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Membership Activity Modal */}
      {showMembershipActivityModal && (
        <div className="modal-overlay" onClick={() => setShowMembershipActivityModal(false)}>
          <div className="modal-content large-modal" onClick={e => e.stopPropagation()} data-testid="membership-activity-modal">
            <div className="modal-header">
              <h2>
                <Activity size={24} style={{ marginRight: '0.5rem' }} />
                Membership Activity
              </h2>
              <button className="modal-close" onClick={() => setShowMembershipActivityModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              {/* Tabs */}
              <div className="activity-tabs" data-testid="activity-tabs">
                <button 
                  className={`activity-tab ${membershipActivityTab === 'new-members' ? 'active' : ''}`}
                  onClick={() => setMembershipActivityTab('new-members')}
                  data-testid="new-members-tab"
                >
                  <UserPlus size={18} />
                  New Members
                  {newMembersCount > 0 && <span className="tab-badge success">{newMembersCount}</span>}
                </button>
                <button 
                  className={`activity-tab ${membershipActivityTab === 'failed-payments' ? 'active' : ''}`}
                  onClick={() => setMembershipActivityTab('failed-payments')}
                  data-testid="failed-payments-tab"
                >
                  <AlertTriangle size={18} />
                  Failed Payments
                  {failedPaymentsCount > 0 && <span className="tab-badge danger">{failedPaymentsCount}</span>}
                </button>
              </div>

              {/* New Members Tab Content */}
              {membershipActivityTab === 'new-members' && (
                <div className="activity-tab-content" data-testid="new-members-content">
                  <p className="modal-description">
                    New members need TradingView indicator access. Mark as processed once you've granted access.
                  </p>

                  {newMembers && newMembers.length > 0 ? (
                    <div className="member-activity-list">
                      {newMembers.map(member => (
                        <div key={member.id} className={`member-card ${member.status}`} data-testid={`new-member-${member.id}`}>
                          <div className="member-card-header">
                            <div className="user-info">
                              <h4>{member.user_name}</h4>
                              <span className="user-email">{member.user_email}</span>
                            </div>
                            <span className={`member-status-badge ${member.status}`}>
                              {member.status === 'pending' ? 'Needs Access' : 'Access Granted'}
                            </span>
                          </div>
                          
                          <div className="member-card-details">
                            <div className="detail-row" style={{ background: member.trading_view_username ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)', padding: '8px', borderRadius: '6px', marginBottom: '8px' }}>
                              <span className="label" style={{ color: member.trading_view_username ? '#10b981' : '#ef4444' }}>TradingView:</span>
                              <span className="value" style={{ color: member.trading_view_username ? '#10b981' : '#ef4444', fontWeight: 'bold' }}>
                                {member.trading_view_username || 'Not provided'}
                              </span>
                            </div>
                            <div className="detail-row">
                              <span className="label">Membership:</span>
                              <span className="value">{member.membership_name}</span>
                            </div>
                            <div className="detail-row">
                              <span className="label">Amount:</span>
                              <span className="value">${member.amount?.toFixed(2)}</span>
                            </div>
                            <div className="detail-row">
                              <span className="label">Joined:</span>
                              <span className="value">{new Date(member.joined_at).toLocaleString()}</span>
                            </div>
                            {member.processed_at && (
                              <div className="detail-row">
                                <span className="label">Processed:</span>
                                <span className="value">{new Date(member.processed_at).toLocaleString()} by {member.processed_by}</span>
                              </div>
                            )}
                          </div>

                          {member.status === 'pending' && (
                            <div className="member-card-actions">
                              <button
                                className="action-btn-primary"
                                onClick={() => handleProcessNewMember(member.id)}
                                data-testid={`grant-access-btn-${member.id}`}
                              >
                                <CheckCircle size={16} />
                                TradingView Access Granted
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <UserPlus size={48} style={{ color: '#10b981', marginBottom: '1rem' }} />
                      <h3>No New Members</h3>
                      <p>New membership signups will appear here.</p>
                    </div>
                  )}

                  {/* Sync and Refresh buttons */}
                  <div className="test-section" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button className="action-btn-primary" onClick={handleSyncNewMemberAlerts}>
                      Sync New Member Alerts
                    </button>
                    <button className="action-btn-primary" onClick={handleRefreshNewMemberData} style={{ background: 'linear-gradient(135deg, #8b5cf6, #6366f1)' }}>
                      Refresh TradingView Names
                    </button>
                    <button className="test-btn" onClick={handleAddTestNewMember}>
                      Add Test Member
                    </button>
                  </div>
                </div>
              )}

              {/* Failed Payments Tab Content */}
              {membershipActivityTab === 'failed-payments' && (
                <div className="activity-tab-content" data-testid="failed-payments-content">
                  <p className="modal-description">
                    Users with failed payments need TradingView access removed. Mark as resolved once done.
                  </p>

                  {failedPayments && failedPayments.length > 0 ? (
                    <div className="member-activity-list">
                      {failedPayments.map(payment => (
                        <div key={payment.id} className={`member-card failed ${payment.status}`} data-testid={`failed-payment-${payment.id}`}>
                          <div className="member-card-header">
                            <div className="user-info">
                              <h4>{payment.user_name}</h4>
                              <span className="user-email">{payment.user_email}</span>
                              {payment.trading_view_username && (
                                <span className="trading-view-username">
                                  TradingView: <strong>{payment.trading_view_username}</strong>
                                </span>
                              )}
                            </div>
                            <span className={`member-status-badge ${payment.status === 'pending' ? 'danger' : 'resolved'}`}>
                              {payment.status === 'pending' ? 'Remove Access' : 'Resolved'}
                            </span>
                          </div>
                          
                          <div className="member-card-details">
                            <div className="detail-row">
                              <span className="label">Membership:</span>
                              <span className="value">{payment.membership_name}</span>
                            </div>
                            <div className="detail-row">
                              <span className="label">Amount:</span>
                              <span className="value">${payment.amount?.toFixed(2)}</span>
                            </div>
                            <div className="detail-row">
                              <span className="label">Failure Reason:</span>
                              <span className="value error">{payment.failure_reason}</span>
                            </div>
                            <div className="detail-row">
                              <span className="label">Failed At:</span>
                              <span className="value">{new Date(payment.failed_at).toLocaleString()}</span>
                            </div>
                            {payment.resolved_at && (
                              <div className="detail-row">
                                <span className="label">Resolved:</span>
                                <span className="value">{new Date(payment.resolved_at).toLocaleString()} by {payment.resolved_by}</span>
                              </div>
                            )}
                          </div>

                          {payment.status === 'pending' && (
                            <div className="member-card-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <button
                                className="action-btn-danger"
                                onClick={() => handleDowngradeUserToFree(payment.user_id, payment.id)}
                                data-testid={`downgrade-to-free-btn-${payment.id}`}
                                style={{ background: 'linear-gradient(135deg, #f97316, #ea580c)' }}
                              >
                                <UserMinus size={16} />
                                Downgrade to Free now
                              </button>
                              <button
                                className="action-btn-danger"
                                onClick={() => handleResolveFailedPayment(payment.id)}
                                data-testid={`remove-access-btn-${payment.id}`}
                              >
                                <UserMinus size={16} />
                                TradingView Access Removed
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <CheckCircle size={48} style={{ color: '#10b981', marginBottom: '1rem' }} />
                      <h3>All Clear!</h3>
                      <p>No failed payments to review.</p>
                    </div>
                  )}

                  {/* Test button */}
                  <div className="test-section">
                    <button className="test-btn" onClick={handleAddTestFailedPayment}>
                      Add Test Failed Payment
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanelPage;